/** Starting point for the "% of orders missed before Voicify" slider when the organization hasn't saved its own. */
export const DEFAULT_MISSED_ORDER_RATE = 0.2;

/** Organization variable holding the organization's saved missed-call rate, as a number from 0 to 1. */
export const MISSED_ORDER_RATE_VARIABLE_NAME = 'SparkRecapturedRevenueMissedOrderRate';

export const parseMissedOrderRate = (value?: string): number | null => {
    const rate = Number(value);
    return value && Number.isFinite(rate) && rate >= 0 && rate <= 1 ? rate : null;
};

export interface RecapturedRevenueEstimateInputs {
    calls: number;
    placedOrders: number;
    averageOrderSize: number;
    isLoading: boolean;
}

/**
 * The calls the store would have missed before Voicify, converted at the assistant's own placed-order rate and average order size:
 * calls × missed % × placed rate × average order size.
 */
export const getRecapturedRevenueEstimate = (inputs: RecapturedRevenueEstimateInputs, missedOrderRate: number) => {
    const calls = inputs.calls || 0;
    const placedRate = calls > 0 ? (inputs.placedOrders || 0) / calls : 0;
    const recapturedOrders = calls * missedOrderRate * placedRate;
    return { placedRate, recapturedOrders, recapturedRevenue: recapturedOrders * (inputs.averageOrderSize || 0) };
};

/** The estimate in words, without the math. */
export const explainRecapturedRevenueEstimate = (missedOrderRate: number) =>
    `Sales from the ${Math.round(missedOrderRate * 100)}% of calls you used to miss, now answered by Voicify.`;

const COMPLEX_MODE_STORAGE_KEY = 'spark.recapturedRevenue.complexMode';

/** Whether this browser last left the recaptured revenue page in complex mode. Storage can be blocked, so it falls back to simple mode. */
export const getStoredRecapturedRevenueComplexMode = () => {
    try {
        return localStorage.getItem(COMPLEX_MODE_STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
};

export const storeRecapturedRevenueComplexMode = (isComplexMode: boolean) => {
    try {
        if (isComplexMode) localStorage.setItem(COMPLEX_MODE_STORAGE_KEY, 'true');
        else localStorage.removeItem(COMPLEX_MODE_STORAGE_KEY);
    } catch {
        // Not remembered when storage is blocked; the mode still applies for this visit.
    }
};
